import { request } from '@core/api';
import type { EntitiesPageResponse } from '@core/api/types';
import { useEffectOnce } from '@core/logic';
import { useAppDispatch } from '@core/redux/hooks';
import { SearchOutline } from '@deepup/icons';
import { iorganizationManagementApiV1 as om } from '@deepup/internal-apis';
import {
  Container,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import type { ActivityStateFilterMode } from '@projects/pages/list/filterProjectsByActivityState';
import type { LabelingFilterMode } from '@projects/pages/list/filterProjectsByLabeling';
import { useProjectLabelingTypes } from '@projects/pages/list/useProjectLabelingTypes';
import { projectActions, projectThunks } from '@projects/redux';
import type { Project } from '@projects/types';
import { useState } from 'react';
import { ProjectListAll } from './ProjectListAll';
import { ProjectListPinned } from './ProjectListPinned';

const allProjectsSize = 2000;

const isPageableResponse = (payload: unknown): payload is EntitiesPageResponse<Project> => {
  return (
    payload !== null &&
    typeof payload === 'object' &&
    'content' in payload &&
    'totalPages' in payload
  );
};

export const ProjectsList = () => {
  const dispatch = useAppDispatch();
  const [search, setSearch] = useState('');
  const theme = useTheme();
  const [labelingFilter, setLabelingFilter] = useState<LabelingFilterMode>('deep_build');
  const [activityStateFilter, setActivityStateFilter] = useState<ActivityStateFilterMode>('all');

  useProjectLabelingTypes();

  const fetchProjects = () => {
    const loadAllPages = async () => {
      try {
        const resultAction = await dispatch(
          projectThunks.getMany({
            path: ['projects'],
            params: {
              page: 0,
              size: allProjectsSize,
              projection: 'scanCount',
            },
          }),
        );

        if (projectThunks.getMany.fulfilled.match(resultAction)) {
          const payload = resultAction.payload;

          if (isPageableResponse(payload)) {
            const totalPages = payload.totalPages ?? 1;

            if (totalPages > 1) {
              const promises = [];
              for (let i = 1; i < totalPages; i++) {
                promises.push(
                  request<EntitiesPageResponse<Project>>('GET', {
                    path: ['projects'],
                    params: {
                      page: i,
                      size: allProjectsSize,
                      projection: 'scanCount',
                    },
                  }),
                );
              }
              const pages = await Promise.all(promises);
              const allProjects = pages.flatMap((page) => page?.content ?? []);
              dispatch(projectActions.upsertMany(allProjects as Project[]));
            }
          }
        }
      } catch (error) {
        console.error('Failed to load all pages of projects:', error);
      }
    };

    loadAllPages();
  };

  useEffectOnce(fetchProjects);

  const onLabelingFilterChange = (e: SelectChangeEvent<LabelingFilterMode>) => {
    setLabelingFilter(e.target.value as LabelingFilterMode);
  };

  const onActivityStateFilterChange = (e: SelectChangeEvent<ActivityStateFilterMode>) => {
    setActivityStateFilter(e.target.value as ActivityStateFilterMode);
  };

  return (
    <Container maxWidth={false}>
      <Stack py={3} gap={3}>
        <Stack
          gap={3}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
        >
          <Typography variant="h4" color="text.primary">
            Projects
          </Typography>
          <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="project-labeling-filter-label">Project labeling type</InputLabel>
              <Select<LabelingFilterMode>
                labelId="project-labeling-filter-label"
                label="Project labeling type"
                value={labelingFilter}
                onChange={onLabelingFilterChange}
              >
                <MenuItem value="deep_build">DeepBuild</MenuItem>
                <MenuItem value="deep_scan">DeepScan</MenuItem>
                <MenuItem value="all">All projects</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="project-activity-state-filter-label">Project state</InputLabel>
              <Select<ActivityStateFilterMode>
                labelId="project-activity-state-filter-label"
                label="Project state"
                value={activityStateFilter}
                onChange={onActivityStateFilterChange}
              >
                <MenuItem value="all">All states</MenuItem>
                <MenuItem value={om.ActivityState.ACTIVE}>Active</MenuItem>
                <MenuItem value={om.ActivityState.NOT_STARTED}>Not started</MenuItem>
                <MenuItem value={om.ActivityState.PAUSED}>Paused</MenuItem>
                <MenuItem value={om.ActivityState.FINISHED}>Finished</MenuItem>
                <MenuItem value={om.ActivityState.CLOSED}>Closed</MenuItem>
              </Select>
            </FormControl>
            <TextField
              type="search"
              placeholder="Search project"
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutline fontSize={24} fill={theme.palette.action.active} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Stack>
        <ProjectListPinned
          search={search}
          labelingFilter={labelingFilter}
          activityStateFilter={activityStateFilter}
        />
        <ProjectListAll
          search={search}
          labelingFilter={labelingFilter}
          activityStateFilter={activityStateFilter}
        />
      </Stack>
    </Container>
  );
};
